/**
 * Validation Mode Component
 * Displays streaming validation results with collapsible articles
 */

import React, { useEffect, useState, RefObject, useCallback } from 'react';
import { 
  Filter20Regular, 
  FilterAdd20Filled,
  ChevronDown20Regular,
  ChevronUp20Regular,
  Search20Regular,
  Dismiss20Regular,
  BookDatabase20Regular,
  BookDatabase20Filled
} from '@fluentui/react-icons';
import { setupArticles } from 'utils/domHelpers';
import { LegislationDocument } from 'services/apiService';
import { FindingEvent } from 'types/findings';
import { FindingCard } from 'components/FindingCard';
import './ValidationMode.css';

interface ValidationModeProps {
  isActive: boolean;
  contentRef: RefObject<HTMLDivElement>;
  legislationDocuments?: LegislationDocument[];
  excludedDocuments?: string[];
  onExcludedDocumentsChange?: (excluded: string[]) => void;
  isLoadingDocuments?: boolean;
  canonicalFindings?: FindingEvent[];
}

export function ValidationMode({ 
  isActive, 
  contentRef, 
  legislationDocuments = [],
  excludedDocuments = [],
  onExcludedDocumentsChange,
  isLoadingDocuments = false,
  canonicalFindings = []
}: ValidationModeProps) {
  const [hasArticles, setHasArticles] = useState(false);
  const [allCollapsed, setAllCollapsed] = useState(false);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [showSearchPopup, setShowSearchPopup] = useState(false);
  const [showDocumentsPopup, setShowDocumentsPopup] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [totalMatches, setTotalMatches] = useState(0);
  const [activeFilters, setActiveFilters] = useState<Set<string>>(
    new Set(['valid', 'undetermined', 'invalid'])
  );

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showFilterPopup && !target.closest('.article-controls')) {
        setShowFilterPopup(false);
      }
      if (showSearchPopup && !target.closest('.article-controls')) {
        setShowSearchPopup(false);
      }
      if (showDocumentsPopup && !target.closest('.article-controls')) {
        setShowDocumentsPopup(false);
      }
    };

    if (showFilterPopup || showSearchPopup || showDocumentsPopup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFilterPopup, showSearchPopup, showDocumentsPopup]);

  const applyFilters = useCallback((filters: Set<string>) => {
    if (!contentRef.current) return;

    // Sprint 3 story #224: canonical <FindingCard> articles were previously
    // invisible to the verdict-filter cluster because they carry
    // `data-filter-key` instead of the legacy `.conclusion` child class. Treat
    // both DOM shapes uniformly so filters, streaming updates and combined
    // search+filter all Just Work regardless of which pipeline produced the
    // node.
    const legacyArticles = contentRef.current.querySelectorAll('.article');
    const findingCards = contentRef.current.querySelectorAll('.finding-card');

    const applyToLegacy = (article: Element) => {
      let shouldShow = true;
      if (filters.size > 0) {
        const conclusion = article.querySelector('.conclusion');
        if (conclusion) {
          shouldShow = Array.from(filters).some(filter =>
            conclusion.classList.contains(filter)
          );
        } else {
          shouldShow = false;
        }
      }
      if (shouldShow && searchText.trim()) {
        const articleText = article.textContent?.toLowerCase() || '';
        shouldShow = articleText.includes(searchText.toLowerCase());
      }
      (article as HTMLElement).style.display = shouldShow ? '' : 'none';
    };

    const applyToFindingCard = (card: Element) => {
      let shouldShow = true;
      if (filters.size > 0) {
        const key = (card as HTMLElement).dataset.filterKey || 'unknown';
        shouldShow = filters.has(key);
      }
      if (shouldShow && searchText.trim()) {
        const cardText = card.textContent?.toLowerCase() || '';
        shouldShow = cardText.includes(searchText.toLowerCase());
      }
      (card as HTMLElement).style.display = shouldShow ? '' : 'none';
    };

    legacyArticles.forEach(applyToLegacy);
    findingCards.forEach(applyToFindingCard);
  }, [contentRef, searchText]);

  useEffect(() => {
    if (isActive && contentRef.current) {
      setupArticles(contentRef.current);
      
      // Check if there are any articles/findings and their collapsed state
      const checkArticles = () => {
        const legacyArticles = Array.from(
          contentRef.current?.querySelectorAll('.article') || []
        );
        const findingCards = Array.from(
          contentRef.current?.querySelectorAll('.finding-card') || []
        );
        const combined = [...legacyArticles, ...findingCards];

        setHasArticles(combined.length > 0);

        if (combined.length > 0) {
          const allAreCollapsed = combined.every(node =>
            node.classList.contains('collapsed') ||
            node.classList.contains('finding-card--collapsed')
          );
          setAllCollapsed(allAreCollapsed);
        }

        // Re-apply filters when articles change
        if (activeFilters.size > 0) {
          applyFilters(activeFilters);
        }
      };

      checkArticles();

      // Use MutationObserver to watch for article additions and class changes
      const observer = new MutationObserver(() => {
        checkArticles();
      });

      observer.observe(contentRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });

      return () => observer.disconnect();
    }
  }, [isActive, contentRef, activeFilters, applyFilters]);

  const handleCollapseAll = () => {
    if (!contentRef.current) return;

    contentRef.current.querySelectorAll('.article').forEach(article => {
      article.classList.add('collapsed');
    });
    contentRef.current.querySelectorAll('.finding-card').forEach(card => {
      card.classList.add('finding-card--collapsed');
    });
    setAllCollapsed(true);
  };

  const handleExpandAll = () => {
    if (!contentRef.current) return;

    contentRef.current.querySelectorAll('.article').forEach(article => {
      article.classList.remove('collapsed');
    });
    contentRef.current.querySelectorAll('.finding-card').forEach(card => {
      card.classList.remove('finding-card--collapsed');
    });
    setAllCollapsed(false);
  };

  const handleScrollToTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleScrollToBottom = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ 
        top: contentRef.current.scrollHeight, 
        behavior: 'smooth' 
      });
    }
  };

  const toggleFilter = (filter: string) => {
    const newFilters = new Set(activeFilters);
    if (newFilters.has(filter)) {
      newFilters.delete(filter);
    } else {
      newFilters.add(filter);
    }
    setActiveFilters(newFilters);
    applyFilters(newFilters);
  };

  const clearFilters = () => {
    setActiveFilters(new Set());
    applyFilters(new Set());
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setCurrentMatchIndex(0);
  };

  const clearSearch = () => {
    setSearchText('');
    setCurrentMatchIndex(0);
    setTotalMatches(0);
    // Remove all highlights
    if (contentRef.current) {
      const highlights = contentRef.current.querySelectorAll('.search-highlight');
      highlights.forEach(highlight => {
        const parent = highlight.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(highlight.textContent || ''), highlight);
          parent.normalize();
        }
      });
    }
  };

  const scrollToMatch = useCallback((index: number) => {
    if (!contentRef.current) return;

    const highlightElements = contentRef.current.querySelectorAll('.search-highlight');
    if (highlightElements.length === 0) return;

    // Filter out highlights in hidden elements and sort by data-match-index
    const highlights = Array.from(highlightElements)
      .filter(el => {
        // Check if element or any parent is hidden
        let current: HTMLElement | null = el as HTMLElement;
        while (current && current !== contentRef.current) {
          const style = window.getComputedStyle(current);
          if (style.display === 'none' || style.visibility === 'hidden') {
            return false;
          }
          current = current.parentElement;
        }
        return true;
      })
      .sort((a, b) => {
        const indexA = parseInt(a.getAttribute('data-match-index') || '0', 10);
        const indexB = parseInt(b.getAttribute('data-match-index') || '0', 10);
        return indexA - indexB;
      });

    if (index >= highlights.length) return;

    // Remove current-match class from all
    highlights.forEach(h => h.classList.remove('current-match'));

    // Add to current match
    const currentHighlight = highlights[index] as HTMLElement;
    if (currentHighlight) {
      currentHighlight.classList.add('current-match');
      
      // Find and expand any collapsed parent articles, element panels or
      // finding-cards (Sprint 3 #224).
      let parent = currentHighlight.parentElement;
      while (parent && parent !== contentRef.current) {
        if (parent.classList.contains('article') && parent.classList.contains('collapsed')) {
          parent.classList.remove('collapsed');
        }
        if (parent.classList.contains('element-panel') && parent.classList.contains('collapsed')) {
          parent.classList.remove('collapsed');
        }
        if (parent.classList.contains('finding-card') && parent.classList.contains('finding-card--collapsed')) {
          parent.classList.remove('finding-card--collapsed');
        }
        parent = parent.parentElement;
      }
      
      // Use scrollIntoView like browser Ctrl+F - simple, instant, and reliable
      currentHighlight.scrollIntoView({
        behavior: 'auto', // Instant scroll like browser find
        block: 'nearest', // Only scroll if needed, like browser behavior
        inline: 'nearest'
      });
    }
  }, [contentRef]);

  const highlightSearchMatches = useCallback(() => {
    if (!contentRef.current || !searchText.trim()) {
      setTotalMatches(0);
      return;
    }

    // Remove existing highlights
    const existingHighlights = contentRef.current.querySelectorAll('.search-highlight');
    existingHighlights.forEach(highlight => {
      const parent = highlight.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(highlight.textContent || ''), highlight);
        parent.normalize();
      }
    });

    const searchLower = searchText.toLowerCase();
    const textNodes: Node[] = [];
    const walker = document.createTreeWalker(
      contentRef.current,
      NodeFilter.SHOW_TEXT,
      null
    );

    let node;
    while ((node = walker.nextNode())) {
      if (node.textContent && node.textContent.toLowerCase().includes(searchLower)) {
        textNodes.push(node);
      }
    }

    let matchCount = 0;
    textNodes.forEach(textNode => {
      const text = textNode.textContent || '';
      const parent = textNode.parentNode;
      if (!parent || parent.nodeName === 'SCRIPT' || parent.nodeName === 'STYLE') return;

      const fragment = document.createDocumentFragment();
      let lastIndex = 0;
      const lowerText = text.toLowerCase();
      let index = lowerText.indexOf(searchLower);

      while (index !== -1) {
        // Add text before match
        if (index > lastIndex) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex, index)));
        }

        // Add highlighted match
        const span = document.createElement('span');
        span.className = 'search-highlight';
        span.textContent = text.slice(index, index + searchText.length);
        span.setAttribute('data-match-index', matchCount.toString());
        fragment.appendChild(span);

        matchCount++;
        lastIndex = index + searchText.length;
        index = lowerText.indexOf(searchLower, lastIndex);
      }

      // Add remaining text
      if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
      }

      parent.replaceChild(fragment, textNode);
    });

    setTotalMatches(matchCount);
    if (matchCount > 0) {
      // Update the count to only visible matches after a brief delay
      // to allow filters to be applied
      setTimeout(() => {
        if (!contentRef.current) return;
        const visibleHighlights = Array.from(
          contentRef.current.querySelectorAll('.search-highlight')
        ).filter(el => {
          let current: HTMLElement | null = el as HTMLElement;
          while (current && current !== contentRef.current) {
            const style = window.getComputedStyle(current);
            if (style.display === 'none' || style.visibility === 'hidden') {
              return false;
            }
            current = current.parentElement;
          }
          return true;
        });
        setTotalMatches(visibleHighlights.length);
        if (visibleHighlights.length > 0) {
          scrollToMatch(0);
        }
      }, 50);
    }
  }, [contentRef, searchText, scrollToMatch]);

  const navigateToNextMatch = () => {
    if (totalMatches === 0) return;
    const nextIndex = (currentMatchIndex + 1) % totalMatches;
    setCurrentMatchIndex(nextIndex);
    scrollToMatch(nextIndex);
  };

  const navigateToPrevMatch = () => {
    if (totalMatches === 0) return;
    const prevIndex = currentMatchIndex === 0 ? totalMatches - 1 : currentMatchIndex - 1;
    setCurrentMatchIndex(prevIndex);
    scrollToMatch(prevIndex);
  };

  // Re-apply filters when search text changes
  useEffect(() => {
    if (hasArticles) {
      applyFilters(activeFilters);
      if (searchText.trim()) {
        highlightSearchMatches();
      } else {
        setTotalMatches(0);
      }
    }
  }, [searchText, hasArticles, activeFilters, applyFilters, highlightSearchMatches]);

  if (!isActive) return null;

  return (
    <div className="validation-mode-container">
      <div className="article-controls">
        <button
          className="control-btn"
          onClick={allCollapsed ? handleExpandAll : handleCollapseAll}
          title={allCollapsed ? "Expand all articles" : "Collapse all articles"}
          aria-label={allCollapsed ? "Expand all articles" : "Collapse all articles"}
          disabled={!hasArticles}
        >
          {allCollapsed ? <ChevronDown20Regular /> : <ChevronUp20Regular />}
        </button>
        <button
          className="control-btn"
          onClick={handleScrollToTop}
          title="Scroll to top"
          aria-label="Scroll to top"
          disabled={!hasArticles}
        >
          ↑
        </button>
        <button
          className="control-btn"
          onClick={handleScrollToBottom}
          title="Scroll to bottom"
          aria-label="Scroll to bottom"
          disabled={!hasArticles}
        >
          ↓
        </button>
        <button
          className={`control-btn filter-btn ${activeFilters.size > 0 ? 'active' : ''}`}
          onClick={() => setShowFilterPopup(!showFilterPopup)}
          title="Filter articles"
          aria-label="Filter articles"
        >
          {activeFilters.size > 0 ? <FilterAdd20Filled /> : <Filter20Regular />}
        </button>
        <button
          className={`control-btn search-btn ${searchText ? 'active' : ''}`}
          onClick={() => setShowSearchPopup(!showSearchPopup)}
          title="Search articles"
          aria-label="Search articles"
          disabled={!hasArticles}
        >
          <Search20Regular />
        </button>
        <button
          className={`control-btn documents-btn ${excludedDocuments.length > 0 ? 'active' : ''}`}
          onClick={() => setShowDocumentsPopup(!showDocumentsPopup)}
          title="Filter legislation documents"
          aria-label="Filter legislation documents"
          disabled={legislationDocuments.length === 0 || isLoadingDocuments}
        >
          {excludedDocuments.length > 0 ? <BookDatabase20Filled /> : <BookDatabase20Regular />}
        </button>
        
        {showSearchPopup && (
          <div className="search-popup">
            <div className="search-header">
              <span>Search Articles</span>
              {searchText && (
                <button 
                  className="clear-search" 
                  onClick={clearSearch}
                  title="Clear search"
                  aria-label="Clear search"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="search-input-container">
              <input
                type="text"
                className="search-input"
                placeholder="Type to search..."
                value={searchText}
                onChange={(e) => handleSearchChange(e.target.value)}
                autoFocus
                aria-label="Search articles text input"
              />
              {searchText && (
                <button 
                  className="clear-input-btn" 
                  onClick={clearSearch}
                  title="Clear search input"
                  aria-label="Clear search input"
                >
                  <Dismiss20Regular />
                </button>
              )}
            </div>
            {searchText && totalMatches > 0 && (
              <div className="search-navigation">
                <button
                  className="nav-btn"
                  onClick={navigateToPrevMatch}
                  title="Previous match"
                  aria-label="Previous match"
                >
                  ↑
                </button>
                <span className="match-counter">
                  {currentMatchIndex + 1} / {totalMatches}
                </span>
                <button
                  className="nav-btn"
                  onClick={navigateToNextMatch}
                  title="Next match"
                  aria-label="Next match"
                >
                  ↓
                </button>
              </div>
            )}
            {searchText && totalMatches === 0 && (
              <div className="no-matches">No matches found</div>
            )}
          </div>
        )}
        
        {showFilterPopup && (
          <div className="filter-popup">
              <div className="filter-header">
                <span>Filter by Status</span>
                {activeFilters.size > 0 && (
                  <button 
                    className="clear-filters" 
                    onClick={clearFilters}
                    title="Clear all filters"
                    aria-label="Clear all filters"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="filter-options">
                <label className="filter-option">
                  <input
                    type="checkbox"
                    checked={activeFilters.has('valid')}
                    onChange={() => toggleFilter('valid')}
                  />
                  <span className="filter-label valid">✓ Compliant</span>
                </label>
                <label className="filter-option">
                  <input
                    type="checkbox"
                    checked={activeFilters.has('undetermined')}
                    onChange={() => toggleFilter('undetermined')}
                  />
                  <span className="filter-label undetermined">? Undetermined</span>
                </label>
                <label className="filter-option">
                  <input
                    type="checkbox"
                    checked={activeFilters.has('invalid')}
                    onChange={() => toggleFilter('invalid')}
                  />
                  <span className="filter-label invalid">✗ Not Compliant</span>
                </label>
                <label className="filter-option">
                  <input
                    type="checkbox"
                    checked={activeFilters.has('not-applicable')}
                    onChange={() => toggleFilter('not-applicable')}
                  />
                  <span className="filter-label not-applicable">− Not Applicable</span>
                </label>
              </div>
            </div>
          )}

        {showDocumentsPopup && (
          <div className="documents-popup">
            <div className="documents-header">
              <span>Filter Legislation Documents</span>
              {excludedDocuments.length > 0 && (
                <button 
                  className="clear-documents" 
                  onClick={() => onExcludedDocumentsChange?.([])}
                  title="Select all documents"
                  aria-label="Select all documents"
                >
                  Select All
                </button>
              )}
            </div>
            <div className="documents-options">
              {isLoadingDocuments ? (
                <div className="documents-loading">Loading documents...</div>
              ) : legislationDocuments.length === 0 ? (
                <div className="documents-empty">No documents available</div>
              ) : (
                legislationDocuments.map((doc, index) => {
                  const isExcluded = excludedDocuments.includes(doc.name);
                  return (
                    <label key={index} className="document-option">
                      <input
                        type="checkbox"
                        checked={!isExcluded}
                        onChange={() => {
                          if (isExcluded) {
                            onExcludedDocumentsChange?.(
                              excludedDocuments.filter(d => d !== doc.name)
                            );
                          } else {
                            onExcludedDocumentsChange?.([...excludedDocuments, doc.name]);
                          }
                        }}
                      />
                      <span className="document-label" title={doc.name}>{doc.name}</span>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
      <div className="content small" ref={contentRef}>
        {canonicalFindings.map(finding => (
          <FindingCard key={finding.id} finding={finding} />
        ))}
      </div>
    </div>
  );
}

export default ValidationMode;
